import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { orpc, rpcClient } from '@/api/rpc/client';
import type {
  ParsePaymentFilesRequest,
  ParsePaymentFilesResponse,
  ImportPaymentsRequest,
  ImportPaymentsResponse,
  GetSuppliersForMappingResponse,
} from '@erp/rpc';
import { useToast } from '@/context/ToastContext';

/**
 * Hook to parse payment CSV file
 * Returns unique suppliers with fuzzy match suggestions
 */
export function useParsePaymentFiles() {
  const { showToast } = useToast();

  return useMutation<
    ParsePaymentFilesResponse,
    Error,
    ParsePaymentFilesRequest
  >({
    mutationFn: async (input) => {
      return await rpcClient.rebates.historicalPaymentImport.parsePaymentFiles(
        input,
      );
    },
    onError: (error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to parse payment file',
        color: 'error',
        icon: 'x',
      });
    },
  });
}

/**
 * Hook to get all suppliers for dropdown
 */
export function useGetSuppliersForMapping() {
  return useQuery({
    ...orpc.rebates.historicalPaymentImport.getSuppliersForMapping.queryOptions(
      {},
    ),
  });
}

/**
 * Hook to import payments with user-confirmed supplier mappings
 */
export function useImportPayments() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation<ImportPaymentsResponse, Error, ImportPaymentsRequest>({
    mutationFn: async (input) => {
      return await rpcClient.rebates.historicalPaymentImport.importPayments(
        input,
      );
    },
    onSuccess: (data) => {
      if (data.success) {
        showToast({
          title: 'Import Complete',
          message: `Created ${data.paymentsCreated} payments. Total: $${data.totalPaymentAmount.toLocaleString()}`,
          color: 'success',
          icon: 'check',
        });

        // Invalidate relevant queries
        void queryClient.invalidateQueries({
          queryKey: ['rebate-agreements'],
        });
        void queryClient.invalidateQueries({
          queryKey: ['supplier-payments'],
        });
      }
    },
    onError: (error) => {
      showToast({
        title: 'Import Failed',
        message: error.message || 'Failed to import payments',
        color: 'error',
        icon: 'x',
      });
    },
  });
}
